import * as React from "react";
import ExperienceSelect from "./form-components/ExperienceSelect";
import Skills from "./form-components/Skills";
import ProTip from "./ProTip";
import { ExperienceForm, EducationForm } from "../../../../shared/forms/experience-education";
import { validateExperience, validateEducation } from "../../utils/validation";

// Context
import { ProfileFormContext } from '../ProfileEditForm';
import { ACTIONS } from './ProfileFormReducer';

export default function Experience() {
  const [state, dispatch] = React.useContext(ProfileFormContext);

  // Set Field
  const setField = (e) =>
    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { [`${e.target.name}`]: e.target.value },
    });

  // Handle experience changes
  const handleExperienceChange = (experiences: any) => {
    // Transform the experiences to the format expected by the Rails backend
    const experiencesAttributes = experiences.map((exp: any) => ({
      id: exp.id,
      employer: exp.company || exp.employer || "",
      title: exp.position || exp.title || "",
      start_date: exp.start_month && exp.start_year ? `${exp.start_year}-${exp.start_month}-01` : exp.start_date || "",
      end_date: exp.end_month && exp.end_year ? `${exp.end_year}-${exp.end_month}-01` : exp.end_date || "",
      description: exp.description || "",
      current: exp.current || false,
      _destroy: exp._destroy || false,
    }));

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { 
        experiences: experiences,
        experiences_attributes: experiencesAttributes 
      },
    });

    // Update validation errors array to match the new experiences
    const updatedNestedErrors = { ...state.nested_validation_errors };
    updatedNestedErrors.experiences = experiences.map((_: any, index: number) => 
      updatedNestedErrors.experiences[index] || {
        employer: null,
        title: null,
        start_date: null,
        end_date: null,
        description: null,
      }
    );
    
    dispatch({
      type: ACTIONS.SET_NESTED_VALIDATION_ERRORS,
      payload: updatedNestedErrors,
    });
  };

  // Handle education changes
  const handleEducationChange = (educations: any) => {
    // Transform the educations to the format expected by the Rails backend
    const educationsAttributes = educations.map((edu: any) => ({
      id: edu.id,
      school_name: edu.school_name || "",
      qualification: edu.qualification || "",
      start_date: edu.start_date || "",
      end_date: edu.end_date || "",
      description: edu.description || "",
      current: edu.current || false,
      _destroy: edu._destroy || false,
    }));

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { 
        educations: educations,
        educations_attributes: educationsAttributes 
      },
    });

    // Update validation errors array to match the new educations
    const updatedNestedErrors = { ...state.nested_validation_errors };
    updatedNestedErrors.educations = educations.map((_: any, index: number) => 
      updatedNestedErrors.educations[index] || {
        school_name: null,
        qualification: null,
        start_date: null,
        end_date: null,
        description: null,
      }
    );
    
    dispatch({
      type: ACTIONS.SET_NESTED_VALIDATION_ERRORS,
      payload: updatedNestedErrors,
    });
  };

  // Handle experience validation
  const handleExperienceValidation = (index: number, field: string, error: string | null) => {
    if (error) {
      dispatch({
        type: ACTIONS.SET_NESTED_FIELD_ERROR,
        payload: { type: 'experiences', index, field, error },
      });
    } else {
      dispatch({
        type: ACTIONS.CLEAR_NESTED_FIELD_ERROR,
        payload: { type: 'experiences', index, field },
      });
    }
  };

  // Handle education validation
  const handleEducationValidation = (index: number, field: string, error: string | null) => {
    if (error) {
      dispatch({
        type: ACTIONS.SET_NESTED_FIELD_ERROR,
        payload: { type: 'educations', index, field, error },
      });
    } else {
      dispatch({
        type: ACTIONS.CLEAR_NESTED_FIELD_ERROR,
        payload: { type: 'educations', index, field },
      });
    }
  };

  // Convert nested validation errors to the format expected by the shared components
  const experienceValidationErrors = {};
  const educationValidationErrors = {};

  // Convert from ProfileEditForm's nested validation structure to shared component format
  if (state.nested_validation_errors?.experiences) {
    state.nested_validation_errors.experiences.forEach((errors, index) => {
      if (errors && Object.values(errors).some(error => error !== null)) {
        experienceValidationErrors[index] = errors;
      }
    });
  }

  if (state.nested_validation_errors?.educations) {
    state.nested_validation_errors.educations.forEach((errors, index) => {
      if (errors && Object.values(errors).some(error => error !== null)) {
        educationValidationErrors[index] = errors;
      }
    });
  }

  return (
    <div>
      <div className="form-section-card">
        <h3><i className="fas fa-tools"></i> Skills & Expertise</h3>
        <Skills />
        <ProTip variant="green" icon="fas fa-star">
          Select all relevant skills to improve your visibility in searches. The more specific skills you add, the better you'll match with suitable positions.
        </ProTip>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-clock"></i> Years of Experience</h3>
        <div className="modern-form-group">
          <ExperienceSelect
            years_experience={state.profile.years_experience}
            setField={setField}
          />
        </div>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-briefcase"></i> Work Experience</h3>
        <ExperienceForm
          experiences={state.profile.experiences || []}
          onExperiencesChange={handleExperienceChange}
          validationErrors={experienceValidationErrors}
          onValidationChange={handleExperienceValidation}
          showValidationErrors={true}
        />
        <ProTip variant="green" icon="fas fa-history">
          Include your most recent dental positions. Focus on achievements and responsibilities that showcase your expertise and patient care skills.
        </ProTip>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-graduation-cap"></i> Education</h3>
        <EducationForm
          educations={state.profile.educations || []}
          onEducationsChange={handleEducationChange}
          validationErrors={educationValidationErrors}
          onValidationChange={handleEducationValidation}
          showValidationErrors={true}
        />
        <ProTip variant="green" icon="fas fa-lightbulb">
          List your dental education and any additional certifications. Recent continuing education courses show your commitment to staying current in the field.
        </ProTip>
      </div>
    </div>
  );
}
